import { takeEvery, takeLatest, call, fork, put, delay, race, select, take } from 'redux-saga/effects';
import {
    Profile,
    EmailTransactionalMessage,
    Sender as SenderModel,
    TransactionalRecipient,
    Template,
    TemplateType,
    TemplateScope,
} from '@dev/ee-api/ee-api-models';
import { safelyEncodeBase64Url } from '@dev/ee-tools';
import dayjs from 'dayjs';
import { cloneDeep, trim } from 'lodash-es';
import { replace } from 'redux-first-history';
import i18n from '@/../i18n';
import { history } from '@shared-store/shared-store';
import takeScreenshot from '@/services/Screenshot';
import { templateToEmail } from '@/services/tools/editor';
import { ValidEmailsState } from '@/store/validemails/types';
import { getCampainCreatorStore } from '@/store/campaignCreator/actions';
import { getGeneralInfo } from '@/store/account/actions';
import { Logger } from '@/services/errors/appLogger';
import {
    loadTemplate as fetchTemplate,
    saveTemplate,
    updateTemplate,
    getDefaultTemplateObj,
} from '../services/ApiCategories/templates';
import { sendEmail } from '../services/ApiCategories/emails';

import {
    ModifyTemplateNameAction,
    GetDefaultTemplateStartAction,
    LoadTemplateAction,
    RawEditorState,
    RawEditorActionTypes,
} from '../store/raweditor/types';
import { LoadingTemplatesSuccess } from '../store/templateList/actions';
import { ApplicationState as State } from '../store';
import {
    ChangeNameSuccess,
    FetchTemplate,
    FetchTemplateSuccess,
    FetchTemplateError,
    SaveChangesError,
    SaveChangesSuccess,
    SaveChanges,
    ChangeToEmail,
    SaveLastScreenshot,
    SaveChangesAfterCloseEditorSuccess,
    CloseTemplateEditorSuccess,
    GetDefaultTemplateSuccess,
    GetDefaultTemplateStart,
    getRawEditorState,
} from '../store/raweditor/actions';
import { LoadValidEmails, LoadValidEmailsSuccess } from '../store/validemails/actions';
import { ShowToast, HideToast } from '../store/toast/actions';
import { TemplateListState } from '../store/templateList/types';
import { TemplatesPaths } from '../services/dictionaries/Paths';

const TAKE_SCREENSHOT_WHEN_CLOSING_EDITOR_TIMEOUT = 3000;

const getProfile = (state: State): Profile => state.account.Profile;
const getToEmail = (state: State): string => state.rawEditor.sendTo;
const getRawEditor = (state: State): RawEditorState => state.rawEditor;
const getTemplateList = (state: State): TemplateListState => state.templateList;
const getValidEmails = (state: State): ValidEmailsState => state.validEmails;

interface PrepareLoadedTemplateFieldsOptions {
    isLoadedNotByOwner: boolean;
    isLoadedAsDefault: boolean;
    template: Template;
    newTemplateName?: string;
    accountEmail?: string;
    isCampaignCreator?: boolean;
}

const prepareLoadedTemplateFields = ({
    isLoadedNotByOwner,
    isLoadedAsDefault,
    template,
    newTemplateName,
    accountEmail,
    isCampaignCreator,
}: PrepareLoadedTemplateFieldsOptions): Template => {
    if (isLoadedNotByOwner && template.IsPublic) {
        const nameWithoutConflict = `${template?.Name ?? ''} ${dayjs().format()}`;
        template.Name = nameWithoutConflict;
    }
    if ((isLoadedAsDefault || isLoadedNotByOwner) && !isCampaignCreator) {
        template.IsPublic = false;
        template.IsDefault = false;
        template.TemplateScope = TemplateScope.Private;
        template.FromEmail = accountEmail ?? '';
    }
    if (isCampaignCreator) {
        template.TemplateScope = TemplateScope.Draft;
    }
    if (newTemplateName) {
        template.Name = newTemplateName;
    }
    return template;
};

function* loadTemplate(action: LoadTemplateAction): any {
    const { payload } = action;
    const { isCampaignCreator, isTemplateFromGallery } = yield select(getRawEditorState);
    const { selectedTemplate, activeTemplateIndex } = yield select(getCampainCreatorStore);

    const activeSelectedTemplate = selectedTemplate?.[activeTemplateIndex];
    const isFromGallery = activeSelectedTemplate?.isFromGallery || isTemplateFromGallery;

    const toEmail = yield select(getToEmail);
    if (!toEmail) {
        const { FirstName = '', LastName = '', Email } = yield select(getProfile);
        yield put(ChangeToEmail(templateToEmail(FirstName, LastName, Email)));
    }
    const validEmails = yield select(getValidEmails);
    if (!validEmails?.emails) {
        yield put(LoadValidEmails());
        yield take(LoadValidEmailsSuccess);
    }

    if (history.location.pathname === TemplatesPaths.EditorLink && payload.isNewTemplate) {
        const urlParams = history.location.search;
        yield put(replace(`${TemplatesPaths.EditorLink}/${safelyEncodeBase64Url(payload.templateName)}${urlParams}`));
        yield put(GetDefaultTemplateStart(TemplateType.RawHTML));
    } else if (isCampaignCreator && payload.isNewTemplate) {
        yield put(GetDefaultTemplateStart(TemplateType.RawHTML));
    } else if (!payload.isNewTemplate) {
        const createNewTemplate = !!payload.isPublic || !!isFromGallery;
        try {
            yield put(FetchTemplate());
            let template = yield call(fetchTemplate, payload.templateName, payload.isPublic);
            const { Email } = yield select(getProfile);
            const { DefaultSender } = yield select(getGeneralInfo);
            template = prepareLoadedTemplateFields({
                isLoadedNotByOwner: payload.isPublic,
                isLoadedAsDefault: false,
                template,
                newTemplateName: isFromGallery
                    ? `${i18n.t('campaign')}: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`
                    : null,
                accountEmail: DefaultSender ?? Email,
                isCampaignCreator,
            });
            yield put(FetchTemplateSuccess(template, payload.templateName, createNewTemplate));
        } catch (e) {
            Logger.error(e?.response?.data?.Error ?? e);
            yield put(FetchTemplateError(undefined, payload.templateName));
            yield put(errorToast(e));
        }
    }
}
function* getDefaultTemplate(action: GetDefaultTemplateStartAction) {
    const { payload } = action;
    try {
        const rawEditor: RawEditorState = yield select(getRawEditor);
        const { templateName } = rawEditor;
        yield put(FetchTemplate());
        const getTemplate = yield call(getDefaultTemplateObj, payload.templateType);
        const { Email } = yield select(getProfile);
        const { DefaultSender } = yield select(getGeneralInfo);
        const template = prepareLoadedTemplateFields({
            isLoadedNotByOwner: true,
            isLoadedAsDefault: true,
            template: getTemplate.data,
            newTemplateName: templateName,
            accountEmail: DefaultSender ?? Email,
        });

        yield put(GetDefaultTemplateSuccess(template));
        yield put(FetchTemplateSuccess(template, rawEditor.templateName, true));
    } catch (e) {
        yield put(errorToast(e));
    }
}

const getDelayValue = (type: string): number => {
    switch (type) {
        case RawEditorActionTypes.CHANGE_TEMPLATE_NAME:
        case RawEditorActionTypes.CHANGE_DEFAULT_OPTIONS:
        case RawEditorActionTypes.MODIFY_AMP_BODY:
        case RawEditorActionTypes.MODIFY_TEXT_BODY:
        case RawEditorActionTypes.MODIFY_SUBJECT:
        case RawEditorActionTypes.MODIFY_FROM:
        case RawEditorActionTypes.CLOSE_EDITOR:
            return 500;
        case RawEditorActionTypes.ADD_TAG:
        case RawEditorActionTypes.REMOVE_TAG:
            return 1000;
        default:
            return 2500;
    }
};

const testEmailSuccessToast = () =>
    ShowToast({
        severity: 'success',
        message: i18n.t('testemailsent'),
        duration: 6000,
        onClose: HideToast,
    });
const errorToast = (e): any =>
    ShowToast({
        severity: 'error',
        message: `Error: ${e.response?.data?.Error}`,
        duration: 6000,
        onClose: HideToast,
    });
const filterTemplates = (templatesList: Template[], tpl: Template): Template[] => {
    const newTemplatesList = templatesList.filter((o) => o.Name !== tpl.Name);
    tpl.MediumScreenshot = tpl.MediumScreenshot && `${tpl.MediumScreenshot}?${dayjs().unix()}`;
    newTemplatesList.unshift(tpl);
    return newTemplatesList;
};

function* changeName(action: ModifyTemplateNameAction): any {
    const rawEditor: RawEditorState = yield select(getRawEditor);
    const { isNewTemplate, templateObject, lastScreenshot, isCampaignCreator } = rawEditor;
    const currentName = templateObject?.Name;
    const newName = trim(action.payload.name);
    const clonedTemplate = cloneDeep(templateObject);

    if (isCampaignCreator) {
        clonedTemplate.TemplateScope = TemplateScope.Draft;
    }

    clonedTemplate.Name = newName;
    yield put(SaveChanges());

    let screenshot = lastScreenshot;
    if (!screenshot) {
        const { previewRef } = action.payload;
        const preview = cloneDeep(previewRef);
        screenshot = yield call(getScreenshot, preview, false, lastScreenshot);
    }

    try {
        if (screenshot) {
            clonedTemplate.Base64Image = screenshot;
        }
        if (isNewTemplate) {
            yield call(saveTemplate, clonedTemplate);
        } else {
            yield call(updateTemplate, currentName, clonedTemplate);
        }
        yield put(ChangeNameSuccess(newName));
        yield put(SaveChangesSuccess());

        if (history.location.pathname === `${TemplatesPaths.EditorLink}/${safelyEncodeBase64Url(currentName)}`) {
            const urlParams = history.location.search;
            const url = `${TemplatesPaths.EditorLink}/${safelyEncodeBase64Url(newName)}${urlParams}`;
            yield put(replace(url));
        }
    } catch (e) {
        yield put(SaveChangesError());
        yield put(errorToast(e));
        Logger.error(e?.response?.data?.Error);
    }
}

function* getScreenshot(preview, isEditorClosed, lastScreenshot) {
    let screenshot = lastScreenshot;
    try {
        if (!isEditorClosed) {
            yield delay(500);
            screenshot = yield takeScreenshot(preview?.current?.contentWindow?.document?.body);
            yield put(SaveLastScreenshot(screenshot));
        } else {
            /* 
                try as quickly as possible take screenshot after close but proceed with save after given timeout
                if lastScreenshot exists it will be used during save
            */
            const { screenshotTaken } = yield race({
                screenshotTaken: takeScreenshot(preview?.current?.contentWindow?.document?.body),
                timeout: delay(TAKE_SCREENSHOT_WHEN_CLOSING_EDITOR_TIMEOUT),
            });
            if (screenshotTaken) {
                screenshot = screenshotTaken;
            } else {
                console.log(
                    `Screenshot not taken in ${TAKE_SCREENSHOT_WHEN_CLOSING_EDITOR_TIMEOUT}ms, using previosuly taken?`,
                    !!screenshot
                );
            }
        }
    } catch (e) {
        console.warn('Screenshot capture interrupted');
    }

    return screenshot;
}

const adjustedScopeAndType = ({
    isCampaignCreator,
    convertToRawHTMLType,
}: Record<'isCampaignCreator' | 'convertToRawHTMLType', boolean>) => {
    const { updatedOptions } =
        [
            {
                updatedOptions: {
                    TemplateScope: TemplateScope.Draft,
                },
                available: isCampaignCreator,
            },
            {
                updatedOptions: {
                    TemplateType: TemplateType.RawHTML,
                },
                available: !isCampaignCreator && convertToRawHTMLType,
            },
        ].find((o) => o.available) || {};
    return updatedOptions;
};

function* replaceURL(isCampaignCreator, name): any {
    if (isCampaignCreator) {
        return;
    }
    const urlParams = history.location.search;

    const newName = safelyEncodeBase64Url(name);
    const url = `${TemplatesPaths.EditorLink}/${newName}${urlParams}`;

    if (history.location.pathname !== `${TemplatesPaths.EditorLink}/${newName}`) {
        yield put(replace(url));
    }
}

function* parseChanges(action): any {
    const rawEditor: RawEditorState = yield select(getRawEditor);
    const { templateObject, lastScreenshot, changesSaved, isNewTemplate, templateName, isCampaignCreator } = rawEditor;
    const clonedTemplate = {
        ...cloneDeep(templateObject),
        ...adjustedScopeAndType({
            isCampaignCreator,
            convertToRawHTMLType: templateObject.TemplateType === TemplateType.TemplateEditor,
        }),
    };

    const { type } = action;
    const { previewRef } = action.payload;
    const preview = cloneDeep(previewRef);
    const isEditorClosed = type === RawEditorActionTypes.CLOSE_EDITOR;

    if (changesSaved || changesSaved === null) {
        if (isEditorClosed) {
            yield put(CloseTemplateEditorSuccess());
        }
        return;
    }

    const screenshot = yield call(getScreenshot, preview, isEditorClosed, lastScreenshot);

    try {
        if (screenshot) {
            clonedTemplate.Base64Image = screenshot;
        }
        yield delay(getDelayValue(type));
        yield put(SaveChanges());
        let savedTemplate;
        if (isNewTemplate) {
            savedTemplate = yield call(saveTemplate, clonedTemplate);

            yield call(replaceURL, isCampaignCreator, clonedTemplate.Name);
        } else {
            savedTemplate = yield call(updateTemplate, templateName, clonedTemplate);
        }

        if (!isEditorClosed) {
            yield put(SaveChangesSuccess());
        } else {
            yield put(SaveChangesAfterCloseEditorSuccess());
            yield put(CloseTemplateEditorSuccess());
        }
        if (savedTemplate && window.location.pathname === TemplatesPaths.TemplateList) {
            const templatesState = yield select(getTemplateList);
            const { templatesList } = templatesState;
            const newTemplatesList = filterTemplates(templatesList, savedTemplate);
            const count = templatesState.count;
            yield put(LoadingTemplatesSuccess(newTemplatesList, count));
        }
    } catch (e) {
        yield put(SaveChangesError());
        yield put(errorToast(e));
        Logger.error(e?.response?.data?.Error);
    }
}

function* send(): any {
    const rawEditor: RawEditorState = yield select(getRawEditor);

    const Sender: SenderModel = {
        From: [rawEditor.templateObject.FromEmail],
        FromName: rawEditor.templateObject.FromName ?? '',
    };

    const Recipients: TransactionalRecipient = {
        To: [rawEditor.sendTo],
    };

    const email: EmailTransactionalMessage = {
        Body: rawEditor.templateObject.Body,
        Sender,
        Recipients,
    };

    try {
        yield call(sendEmail, email);
        yield put(testEmailSuccessToast());
    } catch (e) {
        Logger.error(e);
        yield put(errorToast(e));
        Logger.error(e?.response?.data?.Error);
    }
}

function* saveScreenshot(action): any {
    const preview = action.payload.previewRef;
    const screenshot = yield takeScreenshot(preview?.current?.contentWindow?.document?.body);
    yield put(SaveLastScreenshot(screenshot));
}

function* watchLoadTemplate(): any {
    yield takeEvery(RawEditorActionTypes.LOAD_TEMPLATE, loadTemplate);
}

function* watchSendTest(): any {
    yield takeEvery(RawEditorActionTypes.SEND_TEST_EMAIL, send);
}

function* watchGetDefaultTemplate(): any {
    yield takeEvery(RawEditorActionTypes.GET_DEFAULT_TEMPLATE_START, getDefaultTemplate);
}

function* watchModifyTemplateName(): any {
    yield takeLatest([RawEditorActionTypes.CHANGE_TEMPLATE_NAME], changeName);
}

function* watchSaveScreenshot(): any {
    yield takeLatest([RawEditorActionTypes.SAVE_SCREENSHOT], saveScreenshot);
}

function* watchModifyTemplate(): any {
    yield takeLatest(
        [
            RawEditorActionTypes.REDO_CHANGES,
            RawEditorActionTypes.UNDO_CHANGES,
            RawEditorActionTypes.ADD_TAG,
            RawEditorActionTypes.REMOVE_TAG,
            RawEditorActionTypes.MODIFY_AMP_BODY,
            RawEditorActionTypes.MODIFY_TEXT_BODY,
            RawEditorActionTypes.MODIFY_SUBJECT,
            RawEditorActionTypes.MODIFY_FROM_NAME,
            RawEditorActionTypes.MODIFY_FROM,
            RawEditorActionTypes.SAVE_PAST_BODY,
            RawEditorActionTypes.CHANGE_DEFAULT_OPTIONS,
            RawEditorActionTypes.CLOSE_EDITOR,
        ],
        parseChanges
    );
}

const rawEditorSagas = [
    fork(watchLoadTemplate),
    fork(watchModifyTemplateName),
    fork(watchModifyTemplate),
    fork(watchSendTest),
    fork(watchGetDefaultTemplate),
    fork(watchSaveScreenshot),
];

export default rawEditorSagas;
